import React, { FC, useCallback, useContext } from 'react';
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import { useTranslation } from '../../../translations';
import { NFCTagInterfaceBase } from '../NFCProxy';
import { CashierContext } from '../cashiercontext';
import { NFCTagSerialInterface } from '../nfcInterfaces/NFCTagSerialInterface';
import { setAndOpenSerialPort } from './NFC';

export const ConfigureNFC: FC = () => {
  const { ts } = useTranslation();
  const { state, dispatch } = useContext(CashierContext);

  async function onClickAddSerialNFCTagInterface() {
    try {
      const d = await navigator.serial?.requestPort();
      if (d) {
        const info = d.getInfo();
        const deviceUID = `usbserial:${info.usbVendorId || 'unknown'},${info.usbProductId || 'unknown'}`;
        const newInterface = new NFCTagSerialInterface(deviceUID);
        setAndOpenSerialPort(d, newInterface);
        dispatch({
          type: 'NFC_ADD_INTERFACE',
          source: 'nfc',
          interface: newInterface,
        });

        dispatch({
          type: 'NFC_SET_ACTIVE_INTERFACE',
          source: 'nfc',
          deviceUID,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding NFC tag interface:', error);
    }
  }

  if (!state.nfcScanner) {
    return null;
  }

  const isWebSerialAvailable = navigator.serial !== undefined;

  return (
    <>
      <h4>
        <div>{ts('nfc_tag_interfaces')}</div>
        <UncontrolledDropdown>
          <DropdownToggle caret>{ts('add_manually')}</DropdownToggle>
          <DropdownMenu>
            {isWebSerialAvailable && (
              <DropdownItem key="serialNFCTagInterface" onClick={onClickAddSerialNFCTagInterface}>
                {ts('nfc_tag_interface_with_web_serial')}
              </DropdownItem>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      </h4>
      <div>
        {state.nfcScanner.manager.NFCTagInterfaces.map((NFCTagInterface: NFCTagInterfaceBase) => {
          return (
            <NFCTagInterfaceItem
              NFCTagInterface={NFCTagInterface}
              key={NFCTagInterface.info?.usbProductId}
            />
          );
        })}
      </div>
    </>
  );
};

const NFCTagInterfaceItem: FC<{ readonly NFCTagInterface: NFCTagInterfaceBase }> = ({
  NFCTagInterface,
}) => {
  const { ts } = useTranslation();
  const { updateCashierSettings, config, dispatch } = useContext(CashierContext);

  function onDelete() {
    dispatch({
      type: 'NFC_REMOVE_INTERFACE',
      source: 'nfc',
      interface: NFCTagInterface,
    });
  }

  const setLocalWebProxy = useCallback(() => {
    updateCashierSettings({
      NFCTagInterface: NFCTagInterface.originalDeviceUID,
    });
  }, [updateCashierSettings, NFCTagInterface.originalDeviceUID]);

  const isPrimary = config.NFCTagInterface === NFCTagInterface.originalDeviceUID;

  return (
    <div className="print-settings-printer">
      <div className="print-settings-printer-name">
        <div>
          <strong>{NFCTagInterface.info?.usbProductId || 'Local HTTP Proxy'}</strong>
        </div>
        <div>
          {NFCTagInterface.info?.usbVendorId || 'HTTP Interface'}
          {ts(NFCTagInterface.connected ? 'connected' : 'disconnected')}
        </div>
      </div>
      <div>
        <Button color="primary" disabled={isPrimary} onClick={setLocalWebProxy} outline={isPrimary}>
          {ts(isPrimary ? 'active_as_primary' : 'set_as_primary')}
        </Button>
        {!NFCTagInterface.httpAddress && (
          <Button color="secondary" onClick={onDelete} outline>
            {ts('remove')}
          </Button>
        )}
      </div>
    </div>
  );
};
